import { useContext, useEffect, useState } from 'react';
import axios from "axios";
import { Link, navigate } from 'raviger';
import { Card, Col, ListGroup, Spinner } from 'react-bootstrap';
import { Carousel } from 'react-responsive-carousel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowAltCircleLeft } from '@fortawesome/free-solid-svg-icons';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

import Header from '../components/Header';
import AnimalCoverImage from '../components/AnimalCoverImage';
import AnimalClaimsList from './AnimalClaimsList';
import { AuthContext } from "../accounts/AccountsReducer";
import { SystemErrorContext } from '../components/SystemError';
import { isImageFile } from '../utils/files';

// Standalone claims review page for a single animal: the same information/image
// header as AnimalDetails, with the claims list below it.
function AnimalClaims({ id, incident, organization }) {
  const { state } = useContext(AuthContext);
  const { setShowSystemError } = useContext(SystemErrorContext);

  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [data, setData] = useState({
    id: null,
    id_for_incident: '',
    name: '',
    animal_count: 1,
    status: '',
    species_string_singular: '',
    sex: '',
    age: '',
    size: '',
    pcolor: '',
    scolor: '',
    intake_id: '',
    external_id: '',
    microchip: '',
    front_image: null,
    public: false,
    pll_metadata: {},
  });

  const fetchAnimalData = () => {
    setIsLoading(true);
    axios.get('/animals/api/incident/' + (state ? state.incident.id : 'undefined') + '/animal/' + id + '/')
      .then(response => {
        setData(response.data);
        let image_urls = [];
        image_urls = image_urls.concat(response.data.front_image || []).concat(response.data.side_image || []);
        if (Array.isArray(response.data.extra_images)) {
          image_urls = image_urls.concat(response.data.extra_images.filter(isImageFile));
        }
        setImages(image_urls);
      })
      .catch(() => {
        setShowSystemError(true);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (id) {
      fetchAnimalData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <>
      <Header><span id="back" style={{ cursor: 'pointer' }} onClick={() => state.prevLocation ? navigate(state.prevLocation) : navigate('/' + organization + '/' + incident + '/animals/' + id)} className="me-3"><FontAwesomeIcon icon={faArrowAltCircleLeft} size="lg" inverse /></span>{`Animal #${data.id_for_incident || ' - '} Claims`}</Header>
      <div className="row gx-3 me-0">
        <div className="col-6 d-flex" style={{ marginRight: "0px" }}>
          <Card className="border rounded w-100">
            <Card.Body style={{ marginTop: "-10px", marginBottom: "-6px" }}>
              <div className="d-flex justify-content-between">
                <h4 style={{ paddingTop: "5px" }}>Information</h4>
              </div>
              <p className="phr pt-0 mt-1" />
              <ListGroup variant="flush" style={{ marginTop: "-13px", marginBottom: "-13px" }}>
                <ListGroup.Item>
                  <div className="row">
                    {data.animal_count > 1 ? <span className="col-6"><b>No. of Animals:</b> {data.animal_count}</span> : <span className="col-6"><b>Name:</b> {data.name || "Unknown Name"}</span>}
                    <span className="col-6" style={{ textTransform: "capitalize" }}><b>Status:</b> {data.status}</span>
                  </div>
                </ListGroup.Item>
                <ListGroup.Item style={{ textTransform: "capitalize" }}>
                  <div className="row">
                    <span className="col-6"><b>Species:</b> {data.species_string_singular}</span>
                    <span className="col-6"><b>Sex:</b> {data.sex || "Unknown"}</span>
                  </div>
                </ListGroup.Item>
                <ListGroup.Item style={{ textTransform: "capitalize" }}>
                  <div className="row">
                    <span className="col-6"><b>Age:</b> {data.age || "Unknown"}</span>
                    <span className="col-6"><b>Size:</b> {data.size || "Unknown"}</span>
                  </div>
                </ListGroup.Item>
                <ListGroup.Item style={{ textTransform: "capitalize" }}>
                  <div className="row">
                    <span className="col-6"><b>Primary Color:</b> {data.pcolor || "N/A"}</span>
                    <span className="col-6"><b>Secondary Color:</b> {data.scolor || "N/A"}</span>
                  </div>
                </ListGroup.Item>
                <ListGroup.Item>
                  <div className="row">
                    <span className="col-6"><b>Intake ID:</b> {data.intake_id || "N/A"}</span>
                    <span className="col-6"><b>External ID:</b> {data.external_id || "N/A"}</span>
                  </div>
                </ListGroup.Item>
                <ListGroup.Item>
                  <div className="row">
                    <span className="col-6"><b>Microchip:</b> {data.microchip || "N/A"}</span>
                    <span className="col-6"></span>
                  </div>
                </ListGroup.Item>
                {data.public && (
                  <ListGroup.Item>
                    <b>Public Data:</b>{' '}
                    <Link
                      href={`/public?id=${data.id}`}
                      target="_blank"
                      className="text-link"
                      style={{ textDecoration: "none", color: "white" }}
                      rel="noopener noreferrer"
                    >
                      Public Record
                    </Link>
                    {data.pll_metadata?.imageSearch && (
                      <>
                        {' | '}
                        <Link
                          href={data.pll_metadata.imageSearch}
                          target="_blank"
                          className="text-link"
                          style={{ textDecoration: "none", color: "white" }}
                          rel="noopener noreferrer"
                        >
                          PLL Image Search
                        </Link>
                      </>
                    )}
                    {data.pll_metadata?.proximitySearch && (
                      <>
                        {' | '}
                        <Link
                          href={data.pll_metadata.proximitySearch}
                          target="_blank"
                          className="text-link"
                          style={{ textDecoration: "none", color: "white" }}
                          rel="noopener noreferrer"
                        >
                          PLL Proximity Search
                        </Link>
                      </>
                    )}
                  </ListGroup.Item>
                )}
              </ListGroup>
            </Card.Body>
          </Card>
        </div>
        <Col xs={6} className="pe-0 d-flex flex-column">
          <div className="slide-container flex-grow-1 border rounded ps-0 pe-0" style={{ width: "auto", height: "345px" }}>
            {images.length < 1 ? (
              <Carousel className="carousel-wrapper h-100" showThumbs={false} showStatus={false}>
                <AnimalCoverImage
                  animalSpecies={data.species_string_singular}
                  animalImageSrc={data.front_image}
                  customStyles={{ width: '100%', padding: '40px', height: '343px', borderRadius: '3px' }}
                />
              </Carousel>
            ) : (
              <Carousel className="carousel-wrapper" showThumbs={false} showStatus={false}>
                {images.map(image => (
                  <div key={image} className="image-container">
                    <img src={image} alt="Animal" />
                  </div>
                ))}
              </Carousel>
            )}
          </div>
        </Col>
      </div>
      <div className="row gx-3 me-0 mt-3" style={{ marginBottom: "-15px" }}>
        <div className="col-12 d-flex mb-3">
          <Card className="border rounded d-flex" style={{ width: "100%" }}>
            <Card.Body>
              <div className="d-flex justify-content-between" style={{ marginBottom: "-8px", marginTop: "-10px" }}>
                <h4 style={{ marginBottom: "0px", marginTop: "3px" }}>Claims</h4>
                {isLoading ? <Spinner className="align-self-center me-3" animation="border" size="sm" role="status" variant="light" /> : ""}
              </div>
              <p className="phr mb-0" />
              {data.id ? <AnimalClaimsList animalId={data.id} onApprove={fetchAnimalData} /> : ""}
            </Card.Body>
          </Card>
        </div>
      </div>
    </>
  );
}

export default AnimalClaims;
